import { FieldValues } from 'react-hook-form'
import {
  EntityFieldMap,
  EntityDedupPayload,
  EntityValueArray,
  LayoutResponseType,
  RelationshipFormattedTreeTypes,
  RelationshipResponseTree,
} from '../../types'

export const RELATIONSHIPS = {
  MANY_TO_ONE: 'Many-to-One',
  MANY_TO_MANY: 'Many-to-Many',
  ONE_TO_MANY: 'One-to-Many',
  ONE_TO_ONE: 'One-to-One',
  ROOT: 'root',
}
export const flattenEntityRelationshipTree = (
  obj: RelationshipResponseTree,
  parentEntityId: string | null = null,
  parentName: string | null = null
): RelationshipFormattedTreeTypes[] => {
  const result: RelationshipFormattedTreeTypes[] = []
  const seenFieldNames = new Set<string>() // Set to track already seen field names

  const entityLabel =
    obj.mapped_as === RELATIONSHIPS?.MANY_TO_ONE
      ? obj.singular_label
      : obj.plural_label

  const createEntityName = (
    parentName: string | null,
    fieldName: string | null,
    label: string | null
  ): string => {
    return parentName
      ? `${parentName}.${fieldName || label || 'Unnamed'}`
      : fieldName || label || 'Unnamed'
  }

  // Build the current entity object
  const formattedEntity = {
    entity_id: obj.entity_id || '',
    name: createEntityName(parentName, obj.field_name || '', entityLabel || ''),
    parent_entity_id: parentEntityId,
    mapped_as: obj.mapped_as || RELATIONSHIPS?.ROOT,
    relation_identifier: obj.relation_identifier || null,
    entity_name: entityLabel || '',
    entity: obj?.table_name || '',
    field_name: obj.field_name || obj?.entity_name || '',
    type: obj?.type || '',
    table_name: obj?.entity_name,
  }

  // Add to result if not already added
  if (
    formattedEntity.entity_id &&
    !seenFieldNames.has(formattedEntity.field_name)
  ) {
    result.push(formattedEntity)
    seenFieldNames.add(formattedEntity.field_name) // Mark field_name as seen
  }

  // Recursively process children
  if (obj.children) {
    for (const key in obj.children) {
      if (Object.prototype.hasOwnProperty.call(obj.children, key)) {
        const child = obj.children[key]
        const childName = formattedEntity.name
        const childResults = flattenEntityRelationshipTree(
          child,
          formattedEntity.entity_id,
          childName
        )

        for (const childResult of childResults) {
          if (
            childResult.field_name &&
            !seenFieldNames.has(childResult.field_name)
          ) {
            result.push(childResult)
            seenFieldNames.add(childResult.field_name) // Mark child field_name as seen
          }
        }
      }
    }
  }

  return result
}

export const workflowFieldNameModification = (fieldName: string) => {
  return fieldName?.replace('.', '.children.')
}

const processRelatedEntity = (
  key: string,
  value: EntityFieldMap | EntityValueArray,
  payload: EntityDedupPayload
) => {
  const entityObject = Array.isArray(value) ? value[0] : value
  if (!payload.related_entities[key]) {
    payload.related_entities[key] = {
      select: [],
      filters: [
        {
          criteria: [],
        },
      ],
    }
  }
  for (const [fieldKey, fieldValue] of Object.entries(entityObject)) {
    if (
      fieldKey === '_state' ||
      fieldKey === 'record_id' ||
      fieldKey === 'entity_type'
    )
      continue
    if (fieldValue) payload.related_entities[key].select.push(fieldKey)
    if (fieldValue && typeof fieldValue !== 'object') {
      payload.related_entities[key].filters[0].criteria.push({
        field_name: fieldKey,
        operation: 'EQUALS',
        values: [String(fieldValue)],
      })
    } else if (
      fieldValue &&
      typeof fieldValue === 'object' &&
      'id' in fieldValue
    ) {
      payload.related_entities[key].filters[0].criteria.push({
        field_name: `${fieldKey}_id`,
        operation: 'EQUALS',
        values: [String(fieldValue.id)],
      })
    }
  }
}

export const transformToDuplicateApiPayload = (
  values: FieldValues,
  entityId: string,
  layoutId: string,
  editLayout: LayoutResponseType | undefined
) => {
  const payload: EntityDedupPayload = {
    entity_id: entityId,
    layout_id: layoutId,
    filters: [],
    select: [],
    related_entities: {},
  }

  for (const [key, value] of Object.entries(values)) {
    if (!payload.filters[0]) {
      payload.filters[0] = {
        criteria: [],
      }
    }
    const addedFields = editLayout?.layout_data.added_fields
    if (!addedFields || !(key in addedFields)) continue
    if (value) payload.select.push(key)
    const uiDataType = addedFields[key]?.ui_data_type
    if (
      value &&
      (uiDataType === 'multi lookup' ||
        uiDataType === 'single lookup' ||
        uiDataType === 'category')
    ) {
      processRelatedEntity(key, value, payload)
      continue
    }
    if (value && typeof value !== 'object' && !Array.isArray(value)) {
      payload.filters[0].criteria.push({
        field_name: key,
        operation: 'EQUALS',
        values: [String(value)],
      })
    }
  }
  return payload
}

export const applyProgramScopeToDuplicatePayload = (
  payload: EntityDedupPayload,
  programId?: string | number | null
): void => {
  ;['programs', 'id'].forEach((field) => {
    if (!payload.select.includes(field)) {
      payload.select.push(field)
    }
  })

  if (!payload.related_entities) {
    payload.related_entities = {}
  }

  payload.related_entities.programs = {
    select: ['id'],
    filters: [
      {
        criteria: [
          {
            field_name: 'id',
            operation: 'EQUALS',
            values: [String(programId ?? '')],
          },
        ],
      },
    ],
  }
}
